"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import {
  LOCALE_COOKIE_NAME,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type LocaleCode,
} from "@/i18n/locales";

function setLocaleCookie(code: LocaleCode) {
  // Persist for 1 year; SameSite=Lax prevents CSRF while allowing normal navigation
  document.cookie = `${LOCALE_COOKIE_NAME}=${code}; path=/; max-age=31536000; SameSite=Lax; Secure`;
}

interface LanguageSwitcherProps {
  /** Server-resolved locale — seeded from getLocale() to prevent first-paint flash */
  initialLocale: LocaleCode;
}

export function LanguageSwitcher({ initialLocale }: LanguageSwitcherProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Seed with server-resolved locale so the correct button is active on first paint
  const [current, setCurrent] = useState<LocaleCode>(initialLocale);

  useEffect(() => {
    // Support shared links with ?lang=es-US — takes priority over cookie
    const langParam = searchParams?.get("lang");
    if (langParam && isSupportedLocale(langParam)) {
      setLocaleCookie(langParam);
      setCurrent(langParam);
      // Full navigation ensures server reads the new cookie and renders the
      // correct locale — client-side router.replace() may serve cached RSC payload
      const url = new URL(window.location.href);
      url.searchParams.delete("lang");
      window.location.replace(url.toString());
    }
  }, [searchParams, router]);

  const switchLocale = (code: LocaleCode) => {
    setLocaleCookie(code);
    setCurrent(code);
    router.refresh();
  };

  return (
    <div
      className="flex items-center gap-1 text-sm font-medium"
      role="group"
      aria-label="Language selector"
    >
      {SUPPORTED_LOCALES.map((locale, index) => (
        <span key={locale.code} className="flex items-center gap-1">
          <button
            onClick={() => switchLocale(locale.code)}
            aria-pressed={current === locale.code}
            className={
              current === locale.code
                ? "text-yellow-500"
                : "text-site-foreground hover:text-yellow-500 transition-colors"
            }
          >
            {locale.label}
          </button>
          {index < SUPPORTED_LOCALES.length - 1 && (
            <span className="text-white/30">|</span>
          )}
        </span>
      ))}
    </div>
  );
}
